import React from "react";
import { Trans } from "@lingui/macro";
import * as styles from "./ShipFacilityModal.module.scss";
import { ResponsiveSlideshow } from "components/Slideshow/Slideshow";
import { BlockContent } from "components/BlockContent";
import {
  SanityMultilingualString,
  ShipSuiteModalData_SuiteFragment
} from "types/graphql-types";
import SectionTabs, {
  SectionTabsTab,
  SectionTabsTabList,
  SectionTabsTabPanel
} from "components/SectionTabs/SectionTabs";

import ReadMore from "components/ReadMore/ReadMore";
import {
  Button,
  isSanityImagePresent
} from "@silversea-ssc/ui-components-react";
import PriceFromBox from "components/PriceFromBox/PriceFromBox";
import { CruisePriceFrom, Price } from "types/api/suite_price.d";
import PricePromos from "components/PriceFromBox/PricePromos";
import { LocationSpecificOffer } from "components/useLocationSpecificOffers/types";
import { isPresent } from "src/utils/checks";
import {
  ResponsiveSanityImage,
  SanityImageRatio
} from "@silversea-ssc/ui-components-react";

export interface SuiteModalContentProps {
  suite: ShipSuiteModalData_SuiteFragment;
  price?: CruisePriceFrom;
  offers?: LocationSpecificOffer[];
  raqCTAUrl?: string;
}

const SuiteModalContent: React.FC<SuiteModalContentProps> = ({
  suite,
  price = null,
  offers,
  raqCTAUrl
}) => {
  const title = suite?.card?.title?.localized;
  const description = suite?.description?.localized;
  const images = suite?.images?.filter(isPresent);
  const suiteLocation = suite.location?.filter(isPresent);
  const features = suite?.features?.filter(
    (f): f is SanityMultilingualString =>
      isPresent(f?.localized) && f?.localized != ""
  );

  return (
    <div className={`${styles.container}`}>
      <div className={styles.leftSide}>
        <SectionTabs data-content-name="tabs">
          <div className={styles.tabListBackground}>
            <SectionTabsTabList
              className={styles.tabList}
              spacingBottom="compact"
            >
              <SectionTabsTab
                variant="compact"
                data-content-piece={"suite-overview"}
              >
                <Trans>Suite Overview</Trans>
              </SectionTabsTab>
              <SectionTabsTab
                variant="compact"
                data-content-piece={"suite-plan"}
              >
                <Trans>Suite Plan</Trans>
              </SectionTabsTab>
              <SectionTabsTab
                variant="compact"
                data-content-piece={"suite-features"}
              >
                <Trans>Suite Features</Trans>
              </SectionTabsTab>
              {suite.location && suite.location.length > 0 && (
                <SectionTabsTab
                  variant="compact"
                  data-content-piece={"suite-location"}
                >
                  <Trans>Suite Location</Trans>
                </SectionTabsTab>
              )}
            </SectionTabsTabList>
          </div>
          <SectionTabsTabPanel
            className={styles.panelFixHeight}
            data-content-name="tab_content_overview"
          >
            {images && (
              <ResponsiveSlideshow
                items={images}
                variant="gray"
                fullscreen={false}
                hideCaption={true}
              />
            )}
          </SectionTabsTabPanel>
          <SectionTabsTabPanel
            className={styles.panelFixHeight}
            data-content-name="tab_content_plan"
          >
            {isSanityImagePresent(suite.plan) && (
              <SanityImageRatio ratio="auto" image={suite.plan}>
                <ResponsiveSanityImage
                  image={suite.plan}
                  className={styles.suitePlan}
                  defaultWidth={600}
                  defaultHeight={500}
                  fit="original"
                />
              </SanityImageRatio>
            )}
          </SectionTabsTabPanel>
          <SectionTabsTabPanel
            className={styles.panelFixHeight}
            data-content-name="tab_content_features"
          >
            <ReadMore
              height="6.5em"
              backgroundColor="white"
              openLabel={<Trans>Expand Info</Trans>}
              closeLabel={<Trans>Reduce Info</Trans>}
            >
              <ul className={styles.suiteFeatures}>
                {features &&
                  features.map((feature) => (
                    <li key={feature._key}>
                      <span>{feature.localized}</span>
                    </li>
                  ))}
              </ul>
            </ReadMore>
          </SectionTabsTabPanel>
          {suiteLocation && suiteLocation.length > 0 && (
            <SectionTabsTabPanel
              className={styles.panelFixHeight}
              data-content-name="tab_content_location"
            >
              <ResponsiveSlideshow
                imageFit="contain"
                items={suiteLocation}
                variant="gray"
                fullscreen={false}
                hideCaption={false}
              />
            </SectionTabsTabPanel>
          )}
        </SectionTabs>
      </div>
      <div className={styles.rightSide}>
        <h3>{title}</h3>
        <BlockContent blocks={description} />
        <div className={styles.bedroomsInfo}>
          <BlockContent blocks={suite.bedroomsInfo?.localized} />
        </div>
        {price &&
          ((price as Price).amount > 0 || price.type == "waitlist") &&
          raqCTAUrl && (
            <div className={styles.raqContainer}>
              <PriceFromBox price={price} className={styles.price}>
                {offers && <PricePromos offers={offers} variant="list" />}
              </PriceFromBox>
              <Button
                className={styles.raqCta}
                variant="primary"
                centerText
                href={raqCTAUrl}
                data-content-piece="request_a_quote"
              >
                <Trans>Request a Quote</Trans>
              </Button>
            </div>
          )}
      </div>
    </div>
  );
};

export default SuiteModalContent;
